import { useMemo, useState } from 'react'
import { Check, ChevronsUpDown, X } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover'
import { Avatar } from '../../components/patterns'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../components/ui/command'

export interface HeadCandidate {
  userId: string
  displayName: string
  role?: string | null
  photo?: string | null
}

/**
 * Searchable replacement for the native dept-head <select> — the
 * server has 365+ members now and the old dropdown made finding
 * "Alonso Castillo" a scroll-fest. Built on cmdk + Radix Popover
 * (both already in the bundle); no new deps.
 */
export function HeadSelect({
  value,
  candidates,
  onChange,
  placeholder = '— No head —',
  width = 220,
  disabled = false,
  disabledTitle,
}: {
  value: string | null
  candidates: HeadCandidate[]
  onChange: (userId: string | null) => void
  placeholder?: string
  width?: number
  /** Renders the trigger inert (OSPROD-265: e.g. the "Reports to" picker on
   *  a sub-department — it rolls up via its parent, not a chief). */
  disabled?: boolean
  /** Tooltip explaining WHY the picker is disabled. */
  disabledTitle?: string
}) {
  const [open, setOpen] = useState(false)

  const selected = useMemo(
    () => candidates.find(c => c.userId === value) ?? null,
    [value, candidates],
  )

  return (
    <Popover open={disabled ? false : open} onOpenChange={o => { if (!disabled) setOpen(o) }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          title={disabled ? disabledTitle : undefined}
          className="dept-input flex items-center gap-2 px-2 py-1 text-left bg-card ring-1 ring-border rounded-md hover:bg-accent/40 transition disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-card"
          style={{ width }}
        >
          {selected ? (
            <Avatar name={selected.displayName} src={selected.photo} className="w-5 h-5 text-[10px]" />
          ) : (
            <div className="w-5 h-5 rounded-full bg-muted shrink-0" />
          )}
          <span
            className={`flex-1 truncate text-sm ${
              selected ? 'text-foreground' : 'text-muted-foreground'
            }`}
          >
            {selected?.displayName ?? placeholder}
          </span>
          {selected && !disabled && (
            <span
              role="button"
              tabIndex={0}
              onClick={e => {
                e.stopPropagation()
                onChange(null)
              }}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  e.stopPropagation()
                  onChange(null)
                }
              }}
              className="text-muted-foreground hover:text-foreground shrink-0"
              title="Clear"
              aria-label="Clear head"
            >
              <X size={12} />
            </span>
          )}
          <ChevronsUpDown size={12} className="text-muted-foreground shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <Command
          filter={(_value, search, keywords) => {
            const haystack = (keywords ?? []).join(' ').toLowerCase()
            return haystack.includes(search.toLowerCase()) ? 1 : 0
          }}
        >
          <CommandInput placeholder="Search by name or role…" />
          <CommandList>
            <CommandEmpty>No matches</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__none__"
                keywords={['no head', 'none', 'clear']}
                onSelect={() => {
                  onChange(null)
                  setOpen(false)
                }}
              >
                <div className="w-5 h-5 rounded-full bg-muted shrink-0" />
                <span className="text-muted-foreground italic">{placeholder}</span>
                {!selected && <Check size={14} className="ml-auto text-primary" />}
              </CommandItem>
              {candidates.map(c => (
                <CommandItem
                  key={c.userId}
                  value={c.userId}
                  keywords={[c.displayName, c.role ?? ''].filter(Boolean)}
                  onSelect={() => {
                    onChange(c.userId)
                    setOpen(false)
                  }}
                >
                  <Avatar name={c.displayName} src={c.photo} className="w-5 h-5 text-[10px]" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-foreground truncate">{c.displayName}</div>
                    {c.role && (
                      <div className="text-[10px] text-muted-foreground truncate">{c.role}</div>
                    )}
                  </div>
                  {value === c.userId && <Check size={14} className="ml-auto text-primary" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
